import {WmtsCapabilitiesApiInterface} from "../api/interface/wmts-capabilities";
import {ErrorI18n} from "../i18n";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import type {RevaThunkAction} from "./revaHooks";
import {
    DEFAULT_MATRIXSET,
    LAYER_ALKIS,
    LAYER_BASEMAP,
    LAYER_POIS_DECORATED,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP,
    setCapabilities,
    setLoading,
    setQueryError,
    type WmtsCapabilities,
    type WmtsLayer,
    type WmtsLoadResult,
} from "./wmtsSlice";

/** The GeoServer layers loaded on startup, each with the tile matrix set it is requested for. */
const WMTS_LAYERS: {layer: WmtsLayer; matrixset: string}[] = [
    {layer: LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND, matrixset: DEFAULT_MATRIXSET},
    {layer: LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP, matrixset: DEFAULT_MATRIXSET},
    {layer: LAYER_POIS_DECORATED, matrixset: DEFAULT_MATRIXSET},
    {layer: LAYER_ALKIS, matrixset: DEFAULT_MATRIXSET},
    {layer: LAYER_BASEMAP, matrixset: DEFAULT_MATRIXSET},
];

/**
 * Async thunk to load the GeoServer WMTS capabilities for all known layers.
 *
 * Jeder Layer wird für sich geladen und bewertet (#19906): fällt einer aus (Timeout, 400 „Unknown
 * layer"), landet sein Fehler in `layerErrors`, die übrigen Layer sind trotzdem nutzbar. Nur wenn
 * kein einziger Layer geladen werden konnte, gilt das Laden als fehlgeschlagen (`queryError`).
 */
export const queryCapabilitiesAsync =
    (): RevaThunkAction<Promise<void>> => async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        dispatch(setLoading());
        const layerErrors: WmtsLoadResult["layerErrors"] = {};
        try {
            const credentials = selectCredentials(getState());
            const results = await Promise.allSettled(
                WMTS_LAYERS.map(({layer, matrixset}) =>
                    new WmtsCapabilitiesApiInterface(layer, matrixset).execute(serviceApi, credentials!),
                ),
            );

            const capabilities: WmtsCapabilities = {};
            results.forEach((result, index) => {
                const {layer, matrixset} = WMTS_LAYERS[index];
                if (result.status === "fulfilled") {
                    if (result.value) capabilities[layer] = {capabilities: result.value, matrixset};
                    else layerErrors[layer] = generateDefaultQueryError(new ErrorI18n("general.error.invalid"));
                } else {
                    console.error(`Error loading WMTS capabilities for layer ${layer}`, result.reason);
                    layerErrors[layer] = generateDefaultQueryError(toError(result.reason));
                }
            });

            if (Object.keys(capabilities).length === 0) {
                const firstError = Object.values(layerErrors)[0];
                dispatch(
                    setQueryError({
                        queryError: firstError ?? generateDefaultQueryError(new ErrorI18n("general.error.invalid")),
                        layerErrors,
                    }),
                );
                return;
            }
            dispatch(setCapabilities({capabilities, layerErrors}));
        } catch (err) {
            // Fehler außerhalb der Layer-Abrufe (z. B. fehlende Credentials): ohne diesen Zweig
            // bliebe `loading` für immer gesetzt und die App würde nie freigegeben.
            console.error("Error loading WMTS capabilities", err);
            dispatch(setQueryError({queryError: generateDefaultQueryError(toError(err)), layerErrors}));
        }
    };

function toError(reason: unknown): Error {
    return reason instanceof Error ? reason : new Error(String(reason));
}
